import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { DataState, makeDataState, type StateHolder, withStateLoading } from '@/util/dataState';
import { type ApiUserObject, getCurrentUser } from '@/api/me';
import { taggedLogger } from '@/util/taggedLogger';

const logger = taggedLogger('user-store');

export type CurrentUserState = {
    user: ApiUserObject | null;
} & StateHolder;

export enum HipRoleNames {
    Admin = 'admin',
    OrgAdmin = 'org_admin',
    Surgeon = 'surgeon',
    SurgeonAssist = 'surgeon_assist',
    Sales = 'sales',
    SurgicalSupportEngineer = 'surgical_support_engineer',
}

const makeInitialState = (): CurrentUserState => ({
    user: null,
    ...makeDataState(),
});

export const useUserStore = defineStore('user-store', () => {
    const state = reactive<CurrentUserState>(makeInitialState());

    const userRoles = computed<HipRoleNames[]>(() => state.user?.roles.map((r) => r.name) ?? []);

    const role = computed<HipRoleNames | null>(() => {
        if (userRoles.value.length === 0) {
            return null;
        }
        return userRoles.value[0];
    });

    const isAdmin = computed(() => userRoles.value.includes(HipRoleNames.Admin));
    const isOrgAdmin = computed(() => userRoles.value.includes(HipRoleNames.OrgAdmin));
    const isSurgeon = computed(() => userRoles.value.includes(HipRoleNames.Surgeon));
    const isSurgeonAssist = computed(() => userRoles.value.includes(HipRoleNames.SurgeonAssist));
    const isSales = computed(() => userRoles.value.includes(HipRoleNames.Sales));
    const isSurgicalSupportEngineer = computed(() =>
        userRoles.value.includes(HipRoleNames.SurgicalSupportEngineer),
    );

    const canEditCrNumber = computed(() => isSales.value || isAdmin.value);

    /**
     * Who may order a surgical guide on a role alone.
     *
     * A sales rep may too, but only for a plan of a kind they prepared or already ordered, which
     * is a fact about the plan rather than the caller - so that arm stays where the plan is known.
     * Approving and proposing are absent on purpose: they depend on the caller's relationship to a
     * particular case, which no role name can answer, and the server names that act instead.
     */
    const mayOrderSurgicalGuide = computed(() => isAdmin.value || isSurgeon.value);

    const isLoaded = computed(() => state.state !== DataState.Loading);

    const userId = computed(() => {
        if (state.user !== null) {
            return state.user.id.toString();
        }
        return '';
    });

    // Expose user as a getter for consumers that read store.user directly
    const user = computed({
        get: () => state.user,
        set: (val) => {
            state.user = val;
        },
    });

    async function load(): Promise<void> {
        await withStateLoading(state, async () => {
            state.user = await getCurrentUser();
        });
    }

    function reset(): void {
        logger.debug('Resetting current user information...');
        Object.assign(state, makeInitialState());
    }

    return {
        user,
        userRoles,
        role,
        isAdmin,
        isOrgAdmin,
        isSurgeon,
        isSurgeonAssist,
        isSales,
        isSurgicalSupportEngineer,
        canEditCrNumber,
        mayOrderSurgicalGuide,
        isLoaded,
        userId,
        load,
        reset,
    };
});
